import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Chip, Alert, CircularProgress, InputAdornment,
  Divider, FormControl, Select, MenuItem, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions, Drawer,
  Badge, List, ListItem, ListItemText, IconButton
} from '@mui/material';
import axios from 'axios';
import { API, getAuthHeader, dollars, fmtDate, fmtDay, numField, openAuthenticatedFile } from './shared';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
  { value: 'paid', label: 'Paid' }
];

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'trip_interruption', label: 'Trip interruption' },
  { value: 'tire_wheel', label: 'Tire & wheel' }
];

const typeLabel = (t) => TYPE_OPTIONS.find((o) => o.value === t)?.label || t || '—';

const STATUS_COLOR = { pending: 'warning', approved: 'info', denied: 'error', paid: 'success' };

function StatusChip({ status }) {
  return (
    <Chip
      label={status ? status.charAt(0).toUpperCase() + status.slice(1) : '—'}
      color={STATUS_COLOR[status] || 'default'}
      size="small"
      variant={status === 'pending' ? 'filled' : 'outlined'}
    />
  );
}

const fmtBytes = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

function ActionDialog({ action, claim, onClose, onDone, notify }) {
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!action) return;
    setAmount(claim?.amountRequested ?? '');
    setNotes('');
    setBusy(false);
  }, [action, claim]);

  if (!action || !claim) return null;

  const titles = { approve: 'Approve claim', deny: 'Deny claim', 'mark-paid': 'Mark claim paid' };
  const body = action === 'approve'
    ? { amountApproved: Number(amount), notes }
    : action === 'deny'
      ? { reason: notes }
      : { notes };
  const canSubmit = action === 'approve'
    ? amount !== '' && !Number.isNaN(Number(amount)) && Number(amount) >= 0
    : action === 'deny' ? notes.trim().length > 0 : true;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/customer-admin/claims/${claim.id}/${action}`, body, { headers: getAuthHeader() });
      notify(`Claim ${action === 'approve' ? 'approved' : action === 'deny' ? 'denied' : 'marked paid'}`, 'success');
      onDone(data.claim);
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to update claim', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{titles[action]}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {claim.customer?.name || 'Customer'} · {typeLabel(claim.type)} · requested {dollars(claim.amountRequested)}
        </Typography>
        {action === 'approve' && (
          <TextField
            {...numField({ label: 'Amount approved' })} value={amount} sx={{ mb: 2 }}
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            inputProps={{ min: 0, step: 0.01 }} autoFocus
          />
        )}
        <TextField
          fullWidth size="small" multiline minRows={3}
          label={action === 'deny' ? 'Reason (shown to the customer)' : 'Notes'}
          value={notes} onChange={(e) => setNotes(e.target.value)}
          autoFocus={action !== 'approve'}
          required={action === 'deny'}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained" onClick={submit} disabled={busy || !canSubmit}
          color={action === 'deny' ? 'error' : 'primary'}
        >
          {busy ? 'Saving...' : titles[action]}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ClaimDrawer({ claimId, onClose, onChanged, notify }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [action, setAction] = useState(null);
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    if (!claimId) return;
    setData(null);
    setError('');
    setAction(null);
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/customer-admin/claims/${claimId}`, { headers: getAuthHeader() });
        setData(d);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load claim');
      }
    })();
  }, [claimId]);

  const claim = data?.claim;
  const customer = data?.customer || claim?.customer;
  const primary = data?.primary;
  const job = data?.job;
  // Stable object for the action dialog so its reset effect only fires when the claim actually changes
  const dialogClaim = useMemo(() => (claim ? { ...claim, customer } : null), [claim, customer]);

  const openReceipt = async (index) => {
    setOpening(index);
    try {
      await openAuthenticatedFile(`${API}/api/customer-admin/claims/${claimId}/receipts/${index}`);
    } catch (err) {
      notify(err.message || 'Failed to open receipt', 'error');
    } finally {
      setOpening(null);
    }
  };

  const onActionDone = (updated) => {
    setAction(null);
    setData((prev) => (prev ? { ...prev, claim: { ...prev.claim, ...updated } } : prev));
    onChanged(updated);
  };

  const row = (label, value) => (
    <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>{label}</Typography>
      <Typography variant="body2" component="div" sx={{ flex: 1 }}>{value ?? '—'}</Typography>
    </Box>
  );

  return (
    <Drawer anchor="right" open={!!claimId} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Claim</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Typography component="span" sx={{ fontSize: 20, lineHeight: 1 }}>&times;</Typography>
          </IconButton>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!data && !error && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
        {claim && (
          <>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
              <StatusChip status={claim.status} />
              <Chip label={typeLabel(claim.type)} size="small" variant="outlined" />
              {claim.planName && <Chip label={claim.planName} size="small" variant="outlined" />}
            </Box>

            {row('Customer', customer ? (
              <>
                {customer.name || '—'}<br />
                <Typography variant="caption" color="text.secondary">{[customer.email, customer.phone].filter(Boolean).join(' · ')}</Typography>
              </>
            ) : '—')}
            {primary && row('Household of', `${primary.name || ''}${primary.email ? ` (${primary.email})` : ''}`)}
            {row('Submitted', fmtDate(claim.createdAt))}
            {row('Incident date', fmtDay(claim.incidentDate))}
            {row('Amount requested', dollars(claim.amountRequested))}
            {row('Amount approved', claim.amountApproved != null ? dollars(claim.amountApproved) : '—')}
            {(job || claim.jobPo || claim.jobId) && row('Related job', job
              ? `${job.po || job._id || ''}${job.service ? ` · ${job.service}` : ''}${job.status ? ` (${job.status})` : ''}`
              : (claim.jobPo || claim.jobId))}
            {claim.decidedAt && row('Decided', fmtDate(claim.decidedAt))}
            {claim.paidAt && row('Paid', fmtDate(claim.paidAt))}
            {claim.denialReason && row('Denial reason', claim.denialReason)}
            {claim.notes && row('Notes', claim.notes)}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Description</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{claim.description || '—'}</Typography>

            <Typography variant="subtitle2" gutterBottom>Receipts</Typography>
            {(claim.receipts || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No receipts attached</Typography>
            ) : (
              <List dense disablePadding sx={{ mb: 2 }}>
                {claim.receipts.map((r) => (
                  <ListItem
                    key={r.index}
                    disableGutters
                    secondaryAction={(
                      <Button size="small" onClick={() => openReceipt(r.index)} disabled={opening === r.index}>
                        {opening === r.index ? 'Opening...' : 'Open'}
                      </Button>
                    )}
                  >
                    <ListItemText
                      primary={r.originalName || `Receipt ${r.index + 1}`}
                      secondary={[r.mimeType, fmtBytes(r.size)].filter(Boolean).join(' · ')}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {claim.status === 'pending' && (
                <>
                  <Button variant="contained" onClick={() => setAction('approve')}>Approve</Button>
                  <Button variant="outlined" color="error" onClick={() => setAction('deny')}>Deny</Button>
                </>
              )}
              {claim.status === 'approved' && (
                <Button variant="contained" color="success" onClick={() => setAction('mark-paid')}>Mark paid</Button>
              )}
              {(claim.status === 'denied' || claim.status === 'paid') && (
                <Typography variant="body2" color="text.secondary">No further actions for this claim.</Typography>
              )}
            </Box>
          </>
        )}
      </Box>
      <ActionDialog
        action={action}
        claim={dialogClaim}
        onClose={() => setAction(null)}
        onDone={onActionDone}
        notify={notify}
      />
    </Drawer>
  );
}

function ClaimsSection({ notify }) {
  const [status, setStatus] = useState('pending');
  const [type, setType] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(null);
  const [selected, setSelected] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadCounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/customer-admin/claims/counts`, { headers: getAuthHeader() });
      setPendingCount(data.pending || 0);
    } catch {
      setPendingCount(null);
    }
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ status, type, page: page + 1, limit }).toString();
        const { data } = await axios.get(`${API}/api/customer-admin/claims?${params}`, { headers: getAuthHeader() });
        if (cancelled) return;
        setRows(data.claims || []);
        setTotal(data.total || 0);
      } catch (err) {
        if (!cancelled) notify(err.response?.data?.message || 'Failed to load claims', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [status, type, page, limit, notify, refreshKey]);

  const onClaimChanged = (updated) => {
    setRows((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
    setRefreshKey((k) => k + 1);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Badge badgeContent={pendingCount || 0} color="warning" max={999} sx={{ mr: 1 }}>
          <Typography variant="h6" sx={{ pr: 1.5 }}>Reimbursement claims</Typography>
        </Badge>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="claim-status-label">Status</InputLabel>
          <Select
            labelId="claim-status-label" label="Status" value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          >
            {STATUS_OPTIONS.map((o) => <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="claim-type-label">Type</InputLabel>
          <Select
            labelId="claim-type-label" label="Type" value={type}
            onChange={(e) => { setType(e.target.value); setPage(0); }}
          >
            {TYPE_OPTIONS.map((o) => <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Trip interruption and tire &amp; wheel reimbursements submitted from the customer app. Approve, deny or mark paid from the claim details.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Incident</TableCell>
              <TableCell align="right">Requested</TableCell>
              <TableCell align="right">Approved</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={20} /></TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No claims found</Typography></TableCell></TableRow>
            )}
            {rows.map((c) => (
              <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelected(c.id)}>
                <TableCell>{fmtDay(c.createdAt)}</TableCell>
                <TableCell>
                  {c.customer?.name || '—'}
                  {c.customer?.email && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{c.customer.email}</Typography>
                  )}
                </TableCell>
                <TableCell>{typeLabel(c.type)}</TableCell>
                <TableCell>{fmtDay(c.incidentDate)}</TableCell>
                <TableCell align="right">{dollars(c.amountRequested)}</TableCell>
                <TableCell align="right">{c.amountApproved != null ? dollars(c.amountApproved) : '—'}</TableCell>
                <TableCell><StatusChip status={c.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div" count={total} page={page} rowsPerPage={limit}
        onPageChange={(e, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
      <ClaimDrawer claimId={selected} onClose={() => setSelected(null)} onChanged={onClaimChanged} notify={notify} />
    </Paper>
  );
}

export default ClaimsSection;
